/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState, useEffect} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import {register} from '../core/_requests'
import {Link, useNavigate} from 'react-router-dom'
import {PasswordMeterComponent} from '../../../../_metronic/assets/ts/components'
import CountryDropdown from './CountryDropdown'
import AutocompleteInput from './AutocompleteInput'
import withReactContent from 'sweetalert2-react-content'
import Swal from 'sweetalert2'

const initialValues = {
  username: '',
  institution_name: '',
  institution_category: '',
  country: '',
  email: '',
  password: '',
  changepassword: '',
  gender: '',
}

const genders = ['MALE', 'FEMALE', 'PREFER NOT TO SAY', 'OTHER']

const registrationSchema = Yup.object().shape({
  username: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Username is required'),
  institution_name: Yup.string()
    .max(50, 'Maximum 100 symbols')
    .required('Institution Name is required'),
  institution_category: Yup.string()
    .max(50, 'Maximum 100 symbols')
    .required('Institution Category is required'),
  country: Yup.string().required('Country is required'),
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password is required'),
  changepassword: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Password confirmation is required')
    .oneOf([Yup.ref('password')], "Password and Confirm Password didn't match"),
  gender: Yup.string().oneOf(genders, 'Invalid gender').required('Gender is required'),
})

export function Registration() {
  const ReactSwal = withReactContent(Swal)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const res = await register({
          ...values,
          registration_source: window.location.origin,
        })
        if (res.status === 200) {
          return ReactSwal.fire({
            title: 'Registration success!',
            icon: 'success',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate(`/login/email-verification-sent?email=${values.email}`)
          })
        }
      } catch (error: any) {
        console.error(error)
        const message = error.response?.data?.message || 'The registration details is incorrect'
        setStatus(message)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    PasswordMeterComponent.bootstrap()
  }, [])

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_signup_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-11'>
        {/* begin::Title */}
        <h1 className='text-dark fw-bolder mb-3'>Registration</h1>
        {/* end::Title */}
      </div>

      {formik.status && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      {/* begin::Form group Username */}
      <div className='fv-row mb-5'>
        <label className='form-label fw-bolder text-dark fs-6'>Username</label>
        <input
          placeholder='Username'
          type='text'
          autoComplete='off'
          {...formik.getFieldProps('username')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.username && formik.errors.username,
            },
            {
              'is-valid': formik.touched.username && !formik.errors.username,
            }
          )}
        />
        {formik.touched.username && formik.errors.username && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.username}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group Gender */}
      <div className='fv-row mb-5'>
        <label className='form-label fw-bolder text-dark fs-6'>Gender</label>
        <select
          {...formik.getFieldProps('gender')}
          className={clsx(
            'form-select bg-transparent',
            {
              'is-invalid': formik.touched.gender && formik.errors.gender,
            },
            {
              'is-valid': formik.touched.gender && !formik.errors.gender,
            }
          )}
        >
          <option value=''>Select Gender</option>
          {genders.map((gender) => (
            <option key={gender} value={gender}>{gender}</option>
          ))}
        </select>
        {formik.touched.gender && formik.errors.gender && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.gender}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group Country */}
      <div className='fv-row mb-5'>
        <label className='form-label fw-bolder text-dark fs-6'>Country</label>
        <CountryDropdown
          onSelect={(econ) => formik.setFieldValue('country', econ)}
          econ={formik.values.country}
        />
        {formik.touched.country && formik.errors.country && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.country}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group Institution Name */}
      <div className='fv-row mb-5'>
        <label className='form-label fw-bolder text-dark fs-6'>Institution Name</label>
        <AutocompleteInput
          field='institution_name'
          placeholder='Institution Name'
          value={formik.values.institution_name.toUpperCase()}
          onChange={(val) => formik.setFieldValue('institution_name', val.toUpperCase())}
          onBlur={formik.handleBlur}
          isInvalid={!!(formik.touched.institution_name && formik.errors.institution_name)}
          isValid={!!(formik.touched.institution_name && !formik.errors.institution_name)}
        />
        {formik.touched.institution_name && formik.errors.institution_name && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.institution_name}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group Institution Category */}
      <div className='fv-row mb-5'>
        <label className='form-label fw-bolder text-dark fs-6'>Institution Category</label>
        <AutocompleteInput
          field='institution_category'
          placeholder='Institution Category'
          value={formik.values.institution_category.toUpperCase()}
          onChange={(val) => formik.setFieldValue('institution_category', val.toUpperCase())}
          onBlur={formik.handleBlur}
          isInvalid={!!(formik.touched.institution_category && formik.errors.institution_category)}
          isValid={!!(formik.touched.institution_category && !formik.errors.institution_category)}
        />
        {formik.touched.institution_category && formik.errors.institution_category && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.institution_category}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group Email */}
      <div className='fv-row mb-5'>
        <label className='form-label fw-bolder text-dark fs-6'>Email</label>
        <input
          placeholder='Email'
          type='email'
          autoComplete='off'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group Password */}
      <div className='fv-row mb-5' data-kt-password-meter='true'>
        <div className='mb-1'>
          <label className='form-label fw-bolder text-dark fs-6'>Password</label>
          <div className='text-muted mb-3'>
            Use 8 or more characters with a mix of letters, numbers & symbols.
          </div>
          <div className='position-relative mb-3'>
            <input
              type='password'
              placeholder='Password'
              autoComplete='off'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control bg-transparent',
                {
                  'is-invalid': formik.touched.password && formik.errors.password,
                },
                {
                  'is-valid': formik.touched.password && !formik.errors.password,
                }
              )}
            />
          </div>
          {/* begin::Meter */}
          <div
            className='d-flex align-items-center mb-3'
            data-kt-password-meter-control='highlight'
          >
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px'></div>
          </div>

          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.password}</span>
              </div>
            </div>
          )}
          {/* end::Meter */}
        </div>
      </div>
      {/* end::Form group */}

      {/* begin::Form group Confirm password */}
      <div className='fv-row mb-5'>
        <label className='form-label fw-bolder text-dark fs-6'>Confirm Password</label>
        <input
          type='password'
          placeholder='Password confirmation'
          autoComplete='off'
          {...formik.getFieldProps('changepassword')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.changepassword && formik.errors.changepassword,
            },
            {
              'is-valid': formik.touched.changepassword && !formik.errors.changepassword,
            }
          )}
        />
        {formik.touched.changepassword && formik.errors.changepassword && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.changepassword}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='text-center'>
        <button
          type='submit'
          id='kt_sign_up_submit'
          className='btn btn-lg btn-primary w-100 mb-5'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>Submit</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...{' '}
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <p className='text-center'>
          Alread have an account?{' '}
          <Link to='/login' className='link-primary text-decoration-underline'>
            Login here
          </Link>
        </p>
      </div>
      {/* end::Form group */}
    </form>
  )
}
